/**
 * Vida, Patuá e Fome (doc 02 §7–§9 e §13; doc 07 J2, J3 e I10). Regras puras, executadas só no
 * servidor (o Fôlego, que precisa de predição, fica no `MoveState`). Valores são hipóteses de
 * balanceamento.
 */
export const VITALS = {
  hpMax: 100,
  /** Vida por segundo depois de `hpRegenDelay` sem dano, com Fome ≥ `lowHunger`. */
  hpRegen: 1.0,
  /** Com 0 < Fome < `lowHunger`. */
  hpRegenLow: 0.4,
  hpRegenDelay: 6,
  lowHunger: 25,
  patuaMax: 25,
  patuaRegen: 12,
  patuaDelay: 5,
  /** Depois de quebrar (chegar a 0), o Patuá demora mais para voltar. */
  patuaBrokenDelay: 8,
  hungerMax: 100,
  /** ≈ 14 min de 100 a 0 parado. */
  hungerDecay: 0.12,
  hungerSprintFactor: 3,
  hungerRegenFactor: 1.5,
  /** Afogamento: Vida por segundo com Fôlego zerado e mergulhado (ignora o Patuá). */
  drownPerSecond: 6,
  /** Mitigação saturante: dano × 60/(60 + D), com piso. */
  defenseK: 60,
  minDamageFactor: 0.25,
  /** Um golpe fatal só é contido se a soma Vida + Patuá estava em pelo menos esta fração. */
  fatalThreshold: 0.5,
} as const;

export type FoodEffectId = "regen" | "saciedade" | "folego" | "defesa";

export interface FoodEffect {
  id: FoodEffectId;
  value: number;
  /** Segundos restantes. */
  left: number;
}

export interface Vitals {
  hp: number;
  hpMax: number;
  patua: number;
  patuaMax: number;
  hunger: number;
  hungerMax: number;
  /** Segundos desde o último dano. */
  sinceDamage: number;
  /** O Patuá quebrou e ainda não começou a voltar. */
  patuaBroken: boolean;
  /** Segundos sem regenerar Vida (comida crua). */
  noRegen: number;
  /** Recarga da proteção contra golpe fatal. */
  fatalCooldown: number;
  /** Invulnerável (pós-reviver, F21). */
  invulnerable: number;
  /** Prato ativo (um por vez; o novo substitui). */
  effect: FoodEffect | undefined;
  dead: boolean;
}

export function createVitals(): Vitals {
  return {
    hp: VITALS.hpMax,
    hpMax: VITALS.hpMax,
    patua: VITALS.patuaMax,
    patuaMax: VITALS.patuaMax,
    hunger: VITALS.hungerMax,
    hungerMax: VITALS.hungerMax,
    sinceDamage: VITALS.hpRegenDelay,
    patuaBroken: false,
    noRegen: 0,
    fatalCooldown: 0,
    invulnerable: 0,
    effect: undefined,
    dead: false,
  };
}

export interface VitalsStep {
  dt: number;
  /** Correu neste passo (Fome cai ×3). */
  sprinting: boolean;
  /** Mergulhado com o Fôlego zerado. */
  drowning: boolean;
  /** Multiplica a perda de Fome (Cuscuz). */
  hungerMul?: number;
  /** Vida por segundo extra, com Fome > 0, mesmo em combate (Mel de Jataí). */
  hpRegenBonus?: number;
}

/** A Vida está regenerando agora? (a Fome cai mais rápido enquanto isso) */
export function regenerating(v: Vitals): boolean {
  return !v.dead && v.hp < v.hpMax && v.hunger > 0 && v.noRegen <= 0 && v.sinceDamage >= VITALS.hpRegenDelay;
}

/** Avança os atributos `dt` segundos. Devolve o dano de afogamento aplicado (para feedback). */
export function stepVitals(v: Vitals, step: VitalsStep): number {
  if (v.dead) return 0;
  const { dt } = step;
  v.sinceDamage += dt;
  v.noRegen = Math.max(0, v.noRegen - dt);
  v.fatalCooldown = Math.max(0, v.fatalCooldown - dt);
  v.invulnerable = Math.max(0, v.invulnerable - dt);
  const effect = v.effect;
  if (effect) {
    effect.left -= dt;
    if (effect.left <= 0) v.effect = undefined;
  }

  // Fome.
  const regen = regenerating(v);
  let decay = VITALS.hungerDecay * (step.sprinting ? VITALS.hungerSprintFactor : 1) * (regen ? VITALS.hungerRegenFactor : 1);
  if (v.effect?.id === "saciedade") decay *= 1 - v.effect.value;
  decay *= step.hungerMul ?? 1;
  v.hunger = Math.max(0, v.hunger - decay * dt);

  // Vida: regeneração natural e o prato "regen" (que não depende da Fome).
  if (regen) v.hp += (v.hunger >= VITALS.lowHunger ? VITALS.hpRegen : VITALS.hpRegenLow) * dt;
  if (v.effect?.id === "regen") v.hp += v.effect.value * dt;
  if (step.hpRegenBonus && v.hunger > 0) v.hp += step.hpRegenBonus * dt;
  v.hp = Math.min(v.hpMax, v.hp);

  // Patuá: volta depois de um tempo sem dano (mais tempo se quebrou).
  if (v.patua < v.patuaMax && v.sinceDamage >= (v.patuaBroken ? VITALS.patuaBrokenDelay : VITALS.patuaDelay)) {
    v.patuaBroken = false;
    v.patua = Math.min(v.patuaMax, v.patua + VITALS.patuaRegen * dt);
  }

  if (!step.drowning) return 0;
  const result = applyDamage(v, VITALS.drownPerSecond * dt, { ignorePatua: true, continuous: true });
  return result.dealt;
}

export interface DamageOptions {
  /** Pontos de defesa (armadura, prato "defesa" é somado aqui). */
  defense?: number;
  /** Afogamento: vai direto na Vida. */
  ignorePatua?: boolean;
  /** Dano contínuo (afogamento): sem arredondar nem mínimo de 1. */
  continuous?: boolean;
  /** Recarga da proteção contra golpe fatal, ou `undefined` se desligada (doc 07, J3). */
  fatalProtection?: number | undefined;
}

export interface DamageResult {
  /** Dano que de fato saiu de Patuá + Vida. */
  dealt: number;
  /** Quanto o Patuá absorveu. */
  absorbed: number;
  patuaBroke: boolean;
  /** A proteção contra golpe fatal disparou. */
  saved: boolean;
  died: boolean;
}

/** Mitigação saturante (doc 02 §8): `60/(60 + D)`, piso de 25 %. */
export function mitigate(raw: number, defense: number): number {
  return raw * Math.max(VITALS.minDamageFactor, VITALS.defenseK / (VITALS.defenseK + Math.max(0, defense)));
}

/**
 * Ponto único de entrada de dano (doc 02 §8–§9). Ordem: mitigação → arredondamento (mínimo 1)
 * → proteção contra golpe fatal → Patuá → Vida.
 */
export function applyDamage(v: Vitals, raw: number, options: DamageOptions = {}): DamageResult {
  const none: DamageResult = { dealt: 0, absorbed: 0, patuaBroke: false, saved: false, died: false };
  if (v.dead || v.invulnerable > 0 || !(raw > 0)) return none;
  const defense = (options.defense ?? 0) + (v.effect?.id === "defesa" ? v.effect.value : 0);
  let amount = options.continuous ? raw : Math.max(1, Math.round(mitigate(raw, defense)));
  v.sinceDamage = 0;
  const pool = v.hp + (options.ignorePatua ? 0 : v.patua);
  let saved = false;
  if (
    options.fatalProtection !== undefined &&
    v.fatalCooldown <= 0 &&
    amount >= pool &&
    v.hp + v.patua >= VITALS.fatalThreshold * (v.hpMax + v.patuaMax)
  ) {
    // Fica com 1 de Vida: o golpe leva tudo menos isso.
    amount = pool - 1;
    v.fatalCooldown = options.fatalProtection;
    saved = true;
  }
  let absorbed = 0;
  let patuaBroke = false;
  if (!options.ignorePatua && v.patua > 0) {
    absorbed = Math.min(v.patua, amount);
    v.patua -= absorbed;
    if (v.patua <= 0) {
      v.patua = 0;
      v.patuaBroken = true;
      patuaBroke = true;
    }
  }
  const toHp = Math.min(v.hp, amount - absorbed);
  v.hp -= toHp;
  const died = v.hp <= 0;
  if (died) {
    v.hp = 0;
    v.dead = true;
  }
  return { dealt: absorbed + toHp, absorbed, patuaBroke, saved, died };
}

export interface FoodLike {
  hp: number;
  hunger: number;
  stamina: number;
  effect?: { id: FoodEffectId; value: number; seconds: number };
  inedible?: boolean;
  noRegen?: number;
}

/**
 * Aplica uma mordida (doc 02 §13). Devolve o Fôlego a somar no `MoveState`, ou `undefined` se
 * não se come (morto ou item não comestível).
 */
export function eatFood(v: Vitals, food: FoodLike): number | undefined {
  if (v.dead || food.inedible) return undefined;
  v.hp = Math.min(v.hpMax, v.hp + food.hp);
  v.hunger = Math.min(v.hungerMax, v.hunger + food.hunger);
  if (food.noRegen) v.noRegen = Math.max(v.noRegen, food.noRegen);
  if (food.effect) v.effect = { id: food.effect.id, value: food.effect.value, left: food.effect.seconds };
  return food.stamina;
}

/** Multiplicador da recuperação de Fôlego pelo prato ativo (vai para o `MoveState`). */
export const staminaRegenMul = (v: Vitals): number => (v.effect?.id === "folego" ? 1 + v.effect.value : 1);
